import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ImagePlus, Layers, Video, Warehouse, Settings,
    ChevronsLeft, ChevronsRight, LogOut,
} from 'lucide-react';

export const ERP_NAV_ITEMS = [
    { label: '采购', Icon: Layers,   path: '/erp/purchase',  isActive: (p: string) => p === '/erp/purchase' || p.startsWith('/erp/purchase/') },
    { label: '商品', Icon: Warehouse, path: '/erp/inventory', isActive: (p: string) => p.startsWith('/erp/inventory') },
    { label: '直播', Icon: Video,    path: '/erp/live',      isActive: (p: string) => p.startsWith('/erp/live') },
    { label: '图片工具', Icon: ImagePlus, path: '/erp/image-tools', isActive: (p: string) => p.startsWith('/erp/image-tools') },
    { label: '设置', Icon: Settings,  path: '/erp/settings',  isActive: (p: string) => p.startsWith('/erp/settings') },
];

interface NavItem {
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    path: string | null;
    isActive: (pathname: string) => boolean;
}

interface ErpAdminSidebarProps {
    onLock: () => void;
}

export const ErpAdminSidebar: React.FC<ErpAdminSidebarProps> = ({ onLock }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(() =>
        localStorage.getItem('sseeaa_erp_sidebar_collapsed') === 'true'
    );

    const toggle = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        localStorage.setItem('sseeaa_erp_sidebar_collapsed', String(next));
    };

    return (
        <aside
            className={`flex flex-col bg-white border-r border-slate-100 flex-shrink-0 transition-all duration-300 ${
                isCollapsed ? 'w-14' : 'w-52'
            }`}
            style={{ height: '100%' }}
        >
            {/* Header */}
            <div
                className={`flex items-center h-[60px] flex-shrink-0 ${
                    isCollapsed ? 'justify-center px-2' : 'justify-between pl-5 pr-2'
                }`}
            >
                {!isCollapsed && (
                    <img
                        src="/images/Logo_Color.svg"
                        alt="SSEEAA"
                        className="h-5 w-auto cursor-pointer"
                        onClick={() => navigate('/')}
                    />
                )}
                <button
                    onClick={toggle}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all duration-200"
                    title={isCollapsed ? '展开菜单' : '收起菜单'}
                >
                    {isCollapsed
                        ? <ChevronsRight className="w-4 h-4" />
                        : <ChevronsLeft className="w-4 h-4" />
                    }
                </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-2 px-2">
                {(ERP_NAV_ITEMS as NavItem[]).map(({ label, Icon, path, isActive: matchFn }) => {
                    const isActive = matchFn(location.pathname);
                    return (
                        <button
                            key={label}
                            title={isCollapsed ? label : undefined}
                            onClick={() => { if (path) navigate(path); }}
                            className={`w-full flex items-center h-10 rounded-xl transition-all duration-200 overflow-hidden mb-0.5 cursor-pointer ${
                                isActive
                                    ? 'bg-btn-primary/10 text-btn-primary font-medium'
                                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                            }`}
                        >
                            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                                <Icon className="w-4 h-4" />
                            </div>
                            <div
                                className={`overflow-hidden transition-all duration-300 ${
                                    isCollapsed ? 'w-0 opacity-0' : 'flex-1 opacity-100'
                                }`}
                            >
                                <span className="text-sm whitespace-nowrap pr-4">{label}</span>
                            </div>
                        </button>
                    );
                })}
            </nav>

            {/* Lock / exit */}
            <div className="flex-shrink-0 px-2 pb-4">
                <button
                    onClick={onLock}
                    title={isCollapsed ? '退出管理' : undefined}
                    className="w-full flex items-center h-10 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all duration-200"
                >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <LogOut className="w-4 h-4" />
                    </div>
                    <div
                        className={`overflow-hidden transition-all duration-300 ${
                            isCollapsed ? 'w-0 opacity-0' : 'flex-1 opacity-100'
                        }`}
                    >
                        <span className="text-sm whitespace-nowrap">退出管理</span>
                    </div>
                </button>
            </div>
        </aside>
    );
};
